/**
 * useNotifications — unified notification context
 *
 * Fix: Only send notifications to native ONCE per session, not on every fetch.
 * Track sent IDs to prevent duplicates.
 */
import { createContext, useContext, useEffect, useState, useCallback, useRef, ReactNode } from 'react';
import { useAuth } from './useAuth';

export interface Notification {
  id: number;
  type: string;
  title: string;
  body: string | null;
  url: string | null;
  read: boolean;
  created_at: string | null;
  read_at: string | null;
  /** Ring-only (owner directive 2026-10-10): live server ring state. */
  ring_status?: string | null;
  is_active?: boolean;
}

interface NotificationContextValue {
  notifications: Notification[];
  unreadCount: number;
  connected: boolean;
  markAsRead: (id: number) => void;
  markAllRead: () => void;
}

const NotificationContext = createContext<NotificationContextValue>({
  notifications: [],
  unreadCount: 0,
  connected: false,
  markAsRead: () => {},
  markAllRead: () => {},
});

export function NotificationProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [connected, setConnected] = useState(false);
  const eventSourceRef = useRef<EventSource | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const tokenRef = useRef<string | null>(null);
  const sentToNativeRef = useRef<Set<number>>(new Set());
  const didInitialPushKey = 'saik_notif_initial_push_done';
  const apiBase = '/api/v1';

  useEffect(() => {
    const updateToken = () => {
      tokenRef.current = localStorage.getItem('saik_token');
    };
    updateToken();
    window.addEventListener('storage', updateToken);
    return () => window.removeEventListener('storage', updateToken);
  }, [isAuthenticated]);

  const getToken = () => tokenRef.current;

  const sendTokenToNative = useCallback((tok: string) => {
    try {
      if ((window as any).webkit?.messageHandlers?.saikToken) {
        (window as any).webkit.messageHandlers.saikToken.postMessage(tok);
      }
    } catch(e) {}
  }, []);

  const bridgeToNative = useCallback((notif: Notification) => {
    // Don't re-send notifications that were already pushed
    if (sentToNativeRef.current.has(notif.id)) return;
    // A ring must not become a native banner unless it is a live, active call
    // (owner report 2026-10-10: opening the app fired a burst of stale ring
    // banners/sounds). The ring overlay handles active rings; snoozed/expired
    // rings stay silent rows. Non-ring notifications bridge as before.
    if (notif.type === 'assistant_ring' && !notif.is_active) return;
    // Mid-call (owner directive 2026-10-12): no native ring banners while a live
    // voice session is open — the user is already on the line.
    if (notif.type === 'assistant_ring' && document.documentElement.dataset.saikLiveVoice === '1') return;
    sentToNativeRef.current.add(notif.id);

    document.dispatchEvent(
      new CustomEvent('saik:notification', {
        detail: { type: notif.type, title: notif.title, body: notif.body || '' },
      })
    );
  }, []);

  const fireRing = useCallback((notif: Notification) => {
    if (notif.type !== 'assistant_ring') return;
    // Only an UNANSWERED ring may take the incoming-call screen (owner directive
    // 2026-10-12): 'answered'/'active' are calls that already happened — firing
    // the overlay for them re-"calls" the user mid-conversation or after they
    // hung up. Never fire mid-live-call either; the green bar covers that case.
    const incoming = notif.ring_status === 'ringing' || (notif.is_active === true && !notif.ring_status);
    if (!incoming) return;
    if (document.documentElement.dataset.saikLiveVoice === '1') return;
    try {
      const last = localStorage.getItem('saik_last_ring_id');
      if (last && String(notif.id) === last) return;
      window.dispatchEvent(new CustomEvent('saik:ring', { detail: notif }));
    } catch { /* ignore */ }
  }, []);

  const fetchNotifications = useCallback(async () => {
    const token = getToken();
    if (!token) return;
    try {
      const res = await fetch(`${apiBase}/in-app-notifications?limit=50`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setNotifications(Array.isArray(data) ? data : []);
        sendTokenToNative(token);

        // Only push existing unread notifications to native ONCE (persisted across sessions)
        if (!localStorage.getItem(didInitialPushKey) && Array.isArray(data)) {
          localStorage.setItem(didInitialPushKey, '1');
          const unread = data.filter((n: Notification) => !n.read).slice(0, 5);
          unread.forEach((n: Notification, i: number) => {
            setTimeout(() => bridgeToNative(n), i * 2000);
          });
        }
        // A live "assistant needs you" ring that arrived while the app was
        // closed. 2026-10-09 owner report: opening the app made the phone
        // vibrate/beep/speak "right away" (and it talked over the session before
        // the user answered). A ring is a real-time CALL — it must fire only when
        // it is genuinely fresh, never be replayed for an old unread notification
        // on load. Older unread rings stay as ordinary notifications.
        const RING_FRESH_MS = 120_000;
        const lastAnswered = localStorage.getItem('saik_last_ring_id');
        const latestRing = (Array.isArray(data) ? data : []).find(
          (n: Notification) => {
            if (n.type !== 'assistant_ring' || n.read) return false;
            // Never re-raise the ring the user just answered/dismissed.
            if (lastAnswered && String(n.id) === lastAnswered) return false;
            // Active rings (server-derived) are live calls regardless of age.
            if (n.is_active === true) return true;
            const ts = n.created_at ? Date.parse(n.created_at) : NaN;
            return Number.isFinite(ts) && Date.now() - ts <= RING_FRESH_MS;
          }
        );
        if (latestRing) fireRing(latestRing);
        // Keep the Active Ringers bar in sync on load too.
        window.dispatchEvent(new CustomEvent('saik:rings-refresh'));
      }
    } catch (e) {
      console.error('[SAIK] Failed to fetch notifications:', e);
    }
  }, [apiBase, sendTokenToNative, bridgeToNative, fireRing]);

  const markAsRead = useCallback((id: number) => {
    const token = getToken();
    if (!token) return;
    fetch(`${apiBase}/in-app-notifications/${id}/read`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}` },
    }).catch(() => {});
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
  }, [apiBase]);

  const markAllRead = useCallback(() => {
    const token = getToken();
    if (!token) return;
    fetch(`${apiBase}/in-app-notifications/read-all`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}` },
    }).catch(() => {});
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  }, [apiBase]);

  useEffect(() => {
    if (!isAuthenticated) return;

    const token = getToken();
    if (!token) {
      const retry = setTimeout(() => {
        const t = localStorage.getItem('saik_token');
        if (t) {
          tokenRef.current = t;
          fetchNotifications();
          connectSSE(t);
        }
      }, 500);
      return () => clearTimeout(retry);
    }

    fetchNotifications();
    connectSSE(token);

    function connectSSE(tok: string) {
      const url = `${apiBase}/in-app-notifications/stream?token=${encodeURIComponent(tok)}`;
      const es = new EventSource(url);
      eventSourceRef.current = es;

      es.onopen = () => {
        setConnected(true);
        if (pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      };

      es.addEventListener('notification', (e) => {
        try {
          const data: Notification = JSON.parse(e.data);
          setNotifications((prev) =>
            prev.some((n) => n.id === data.id) ? prev : [data, ...prev]
          );
          bridgeToNative(data);
          fireRing(data);
          // Active Ringers (2026-10-10): any ring activity (a new ring or a ring
          // state change) tells the green bar + panel to re-sync.
          if (data.type === 'assistant_ring' || (data as { ring_update?: boolean }).ring_update) {
            window.dispatchEvent(new CustomEvent('saik:rings-refresh'));
          }
        } catch (err) {
          console.error('[SAIK] SSE parse error:', err);
        }
      });

      es.onerror = () => {
        setConnected(false);
        es.close();
        eventSourceRef.current = null;
        if (!pollRef.current) {
          pollRef.current = setInterval(() => fetchNotifications(), 30000);
        }
      };
    }

    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [isAuthenticated, apiBase, fetchNotifications, bridgeToNative, fireRing]);

  const unreadCount = notifications.filter((n) => !n.read).length;

  return (
    <NotificationContext.Provider value={{ notifications, unreadCount, connected, markAsRead, markAllRead }}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  return useContext(NotificationContext);
}